import React, { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import Layout from '../components/Layout';
import { useAuth } from '../contexts/AuthContext';
import {
  GivingBreakdown,
  MbcbReportPayload,
  WeeklyMetricRow,
  getMbcbReportDetail,
  submitMbcbReport,
  updateMbcbReport,
} from '../api/plantersService';
import {
  canEditMbcbReport,
  canManageMbcbReportForPlanter,
  formatMbcbReportPeriod,
  mbcbReportStatusLabel,
} from '../utils/mbcbReports';
import {
  sumMbcbBudgetReceiptCategories,
  sumMbcbMissionGivingCategories,
} from '../utils/mbcbGivingBreakdown';

type SundayKey = 's1' | 's2' | 's3' | 's4' | 's5';

const SUNDAYS: SundayKey[] = ['s1', 's2', 's3', 's4', 's5'];

const emptySundayRow = (): Record<SundayKey, number> => ({
  s1: 0,
  s2: 0,
  s3: 0,
  s4: 0,
  s5: 0,
});

const defaultWeekly = (): WeeklyMetricRow => ({
  worshipAttendance: emptySundayRow(),
});

const defaultGiving = (): GivingBreakdown => ({
  income: {
    congregationalOffering: null,
    sponsoringChurches: null,
    associations: null,
    mbcb: null,
    otherSupport: null,
    total: null,
  },
  missionGiving: {
    cooperativeProgram: null,
    associations: null,
    margaretLackey: null,
    otherMissionCauses: null,
    total: null,
  },
});

const mergeWeekly = (raw?: Partial<WeeklyMetricRow> | Record<string, unknown>): WeeklyMetricRow => {
  const base = defaultWeekly();
  if (!raw) return base;
  const legacy = raw as Record<string, unknown>;
  const row = raw.worshipAttendance ?? legacy.worshipAttendance;
  if (!row || typeof row !== 'object') return base;
  SUNDAYS.forEach((sk) => {
    const value = (row as Record<string, unknown>)[sk];
    base.worshipAttendance[sk] =
      typeof value === 'number' && !Number.isNaN(value) ? value : 0;
  });
  return base;
};

const mergeGiving = (raw?: Partial<GivingBreakdown>): GivingBreakdown => {
  const base = defaultGiving();
  if (!raw) return base;
  if (raw.income) Object.assign(base.income, raw.income);
  if (raw.missionGiving) Object.assign(base.missionGiving, raw.missionGiving);
  return base;
};

const sumWorship = (weekly: WeeklyMetricRow) =>
  SUNDAYS.reduce((acc, sk) => acc + (weekly.worshipAttendance[sk] || 0), 0);

const buildDuplicateConflict = (error: unknown) => {
  const payload = (error as {
    response?: { status?: number; data?: { code?: string; data?: { existingReportId?: number } } };
  })?.response;
  if (payload?.status !== 409 || payload.data?.code !== 'REPORT_PERIOD_EXISTS') return null;
  return payload.data.data?.existingReportId ?? null;
};

const PlanterMbcbReportEditor: React.FC = () => {
  const { id, reportId } = useParams<{ id: string; reportId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuth();

  const planterId = Number.parseInt(id ?? '', 10);
  const rid = Number.parseInt(reportId ?? '', 10);
  const roleNames = (user?.roles ?? []).map((role) => (typeof role === 'string' ? role : role.name));

  const [month, setMonth] = useState<number>(new Date().getMonth() + 1);
  const [year, setYear] = useState<number>(new Date().getFullYear());
  const [weekly, setWeekly] = useState<WeeklyMetricRow>(defaultWeekly);
  const [giving, setGiving] = useState<GivingBreakdown>(defaultGiving);
  const [newMembers, setNewMembers] = useState(0);
  const [baptisms, setBaptisms] = useState(0);
  const [salvations, setSalvations] = useState(0);
  const [ministryHighlights, setMinistryHighlights] = useState('');
  const [prayerRequests, setPrayerRequests] = useState('');
  const [supportNeeded, setSupportNeeded] = useState('');
  const [additionalNotes, setAdditionalNotes] = useState('');
  const [nextMonthFocus, setNextMonthFocus] = useState('');
  const [attendanceNotes, setAttendanceNotes] = useState('');
  const [givingNotes, setGivingNotes] = useState('');
  const [saveMessage, setSaveMessage] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);

  const { data: report, isLoading, error } = useQuery({
    queryKey: ['mbcbReport', rid],
    queryFn: () => getMbcbReportDetail(rid),
    enabled: Number.isFinite(rid) && rid > 0,
  });

  useEffect(() => {
    if (!report) return;
    setMonth(report.reportMonth);
    setYear(report.reportYear);
    setWeekly(mergeWeekly(report.weeklyMetrics));
    setGiving(mergeGiving(report.givingBreakdown));
    setNewMembers(report.newMembers ?? 0);
    setBaptisms(report.baptisms ?? 0);
    setSalvations(report.salvations ?? 0);
    setMinistryHighlights(report.ministryHighlights || '');
    setPrayerRequests(report.prayerRequests || '');
    setSupportNeeded(report.supportNeeded || '');
    setAdditionalNotes(report.additionalNotes || '');
    setNextMonthFocus(report.nextMonthFocus || '');
    setAttendanceNotes(report.attendanceNotes || '');
    setGivingNotes(report.givingNotes || '');
  }, [report]);

  const canEdit = useMemo(() => {
    if (!report) return false;
    return canEditMbcbReport(
      report.status,
      canManageMbcbReportForPlanter(user?.id, report.planter?.assignedHubLeader, roleNames),
    );
  }, [report, user?.id, roleNames]);

  const canSubmit = useMemo(() => {
    if (!report) return false;
    return report.status === 'draft' || report.status === 'needs_revision';
  }, [report]);
  const reportPeriodLabel = formatMbcbReportPeriod(month, year);

  const budgetReceiptsTotal = useMemo(
    () => sumMbcbBudgetReceiptCategories(giving.income),
    [giving.income],
  );
  const missionGivingTotal = useMemo(
    () => sumMbcbMissionGivingCategories(giving.missionGiving),
    [giving.missionGiving],
  );

  const setSunday = (sunday: SundayKey, value: string) => {
    const parsed = Number.parseInt(value, 10);
    setWeekly((prev) => ({
      ...prev,
      worshipAttendance: {
        ...prev.worshipAttendance,
        [sunday]: Number.isFinite(parsed) && parsed >= 0 ? parsed : 0,
      },
    }));
  };

  const buildPayload = (): MbcbReportPayload => {
    const incomeTotal = sumMbcbBudgetReceiptCategories(giving.income);
    const missionTotal = sumMbcbMissionGivingCategories(giving.missionGiving);
    return {
      reportMonth: month,
      reportYear: year,
      sundayAttendance: sumWorship(weekly),
      smallGroupAttendance: 0,
      newMembers,
      baptisms,
      salvations,
      monthlyIncome: incomeTotal,
      monthlyExpenses: 0,
      buildingFund: 0,
      missionsGiving: missionTotal,
      ministryHighlights: ministryHighlights || null,
      prayerRequests: prayerRequests || null,
      supportNeeded: supportNeeded || null,
      additionalNotes: additionalNotes || null,
      nextMonthFocus: nextMonthFocus || null,
      attendanceNotes: attendanceNotes || null,
      givingNotes: givingNotes || null,
      weeklyMetrics: { worshipAttendance: weekly.worshipAttendance },
      givingBreakdown: {
        ...giving,
        income: { ...giving.income, total: incomeTotal },
        missionGiving: { ...giving.missionGiving, total: missionTotal },
      },
    };
  };

  const handleDuplicateConflict = (existingReportId: number | null) => {
    if (!existingReportId) {
      setFormError('A report already exists for that planter and month.');
      return;
    }
    setFormError('A report already exists for that planter and month. Opening the existing report.');
    navigate(`/planters/${planterId}/reports/${existingReportId}`);
  };

  const saveMutation = useMutation({
    mutationFn: async () => updateMbcbReport(rid, buildPayload()),
    onSuccess: (updated) => {
      queryClient.invalidateQueries({ queryKey: ['planterMbcbReports', planterId] });
      queryClient.invalidateQueries({ queryKey: ['mbcbReport', rid] });
      queryClient.setQueryData(['mbcbReport', rid], updated);
      setFormError(null);
      setSaveMessage('Saved draft');
      window.setTimeout(() => setSaveMessage(null), 2500);
    },
    onError: (mutationError) => {
      const existingReportId = buildDuplicateConflict(mutationError);
      if (existingReportId !== null) {
        handleDuplicateConflict(existingReportId);
        return;
      }
      setFormError('Could not save this report.');
    },
  });

  const submitMutation = useMutation({
    mutationFn: async () => {
      await updateMbcbReport(rid, buildPayload());
      return submitMbcbReport(rid);
    },
    onSuccess: (submitted) => {
      queryClient.invalidateQueries({ queryKey: ['planterMbcbReports', planterId] });
      queryClient.invalidateQueries({ queryKey: ['mbcbReport', rid] });
      queryClient.setQueryData(['mbcbReport', rid], submitted);
      setFormError(null);
      setSaveMessage('Report submitted');
      window.setTimeout(() => setSaveMessage(null), 3000);
    },
    onError: (mutationError) => {
      const existingReportId = buildDuplicateConflict(mutationError);
      if (existingReportId !== null) {
        handleDuplicateConflict(existingReportId);
        return;
      }
      setFormError('Could not submit this report.');
    },
  });

  const incomeField = (key: keyof GivingBreakdown['income'], label: string) => {
    if (key === 'total') return null;
    return (
      <div key={key}>
        <label className="block text-xs font-medium text-gray-600 mb-0.5">{label}</label>
        <input
          type="number"
          min={0}
          step="0.01"
          disabled={!canEdit}
          value={giving.income[key] ?? ''}
          onChange={(e) => {
            const value = e.target.value;
            setGiving((prev) => ({
              ...prev,
              income: {
                ...prev.income,
                [key]: value === '' ? null : parseFloat(value),
              },
            }));
          }}
          className="block w-full rounded-md border-gray-300 text-sm shadow-sm"
        />
      </div>
    );
  };

  const missionField = (key: keyof GivingBreakdown['missionGiving'], label: string) => {
    if (key === 'total') return null;
    return (
      <div key={key}>
        <label className="block text-xs font-medium text-gray-600 mb-0.5">{label}</label>
        <input
          type="number"
          min={0}
          step="0.01"
          disabled={!canEdit}
          value={giving.missionGiving[key] ?? ''}
          onChange={(e) => {
            const value = e.target.value;
            setGiving((prev) => ({
              ...prev,
              missionGiving: {
                ...prev.missionGiving,
                [key]: value === '' ? null : parseFloat(value),
              },
            }));
          }}
          className="block w-full rounded-md border-gray-300 text-sm shadow-sm"
        />
      </div>
    );
  };

  const worshipBySundaySection = () => (
    <div className="mb-4">
      <h4 className="text-sm font-semibold text-gray-800 mb-2">Worship attendance</h4>
      <div className="grid grid-cols-5 gap-2">
        {SUNDAYS.map((sk, idx) => (
          <div key={sk}>
            <label className="block text-xs text-gray-500 mb-0.5">{`Sun ${idx + 1}`}</label>
            <input
              type="number"
              min={0}
              disabled={!canEdit}
              value={weekly.worshipAttendance[sk]}
              onChange={(e) => setSunday(sk, e.target.value)}
              className="block w-full rounded-md border-gray-300 text-sm"
            />
          </div>
        ))}
      </div>
    </div>
  );

  if (!Number.isFinite(rid) || rid <= 0) {
    return (
      <Layout>
        <div className="p-6">Invalid report.</div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="px-4 sm:px-0 max-w-5xl">
        <button
          type="button"
          onClick={() => navigate(`/planters/${planterId}`)}
          className="text-sm text-gray-600 hover:text-gray-900 mb-4 flex items-center"
        >
          ← Back to planter
        </button>

        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900">{reportPeriodLabel} report</h1>
          <p className="text-sm text-gray-600 mt-1">
            Help the planter share their month—story first, then a light snapshot of numbers. One report per planter per calendar month.
          </p>
        </div>

        {isLoading && <p className="text-sm text-gray-500">Loading report…</p>}
        {error && (
          <p className="text-sm text-red-600">
            You may not have access to this report, or it no longer exists.
          </p>
        )}

        {report && (
          <div className="bg-white shadow rounded-lg p-6 space-y-8">
            <div className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-100 pb-4">
              <div>
                <p className="text-sm text-gray-500">
                  {report.planter?.name || 'Planter'} {report.planter?.churchName ? `· ${report.planter.churchName}` : ''}
                </p>
                <p className="mt-1 text-lg font-semibold text-gray-900">{reportPeriodLabel}</p>
                <div className="mt-2 flex flex-wrap gap-4">
                  <label className="text-sm text-gray-700">
                    <span className="block text-xs text-gray-500 mb-1">Month</span>
                    <input
                      type="number"
                      min={1}
                      max={12}
                      disabled={!canEdit}
                      value={month}
                      onChange={(e) => setMonth(Math.max(1, Math.min(12, Number.parseInt(e.target.value, 10) || 1)))}
                      className="w-24 rounded-md border-gray-300 text-sm"
                    />
                  </label>
                  <label className="text-sm text-gray-700">
                    <span className="block text-xs text-gray-500 mb-1">Year</span>
                    <input
                      type="number"
                      min={2000}
                      max={2100}
                      disabled={!canEdit}
                      value={year}
                      onChange={(e) => setYear(Number.parseInt(e.target.value, 10) || new Date().getFullYear())}
                      className="w-28 rounded-md border-gray-300 text-sm"
                    />
                  </label>
                </div>
              </div>
              <div className="text-sm">
                <div>
                  <span className="text-gray-500">Status </span>
                  <span className="font-medium text-gray-900">{mbcbReportStatusLabel(report.status)}</span>
                </div>
                {report.submittedAt && (
                  <div className="text-gray-500 mt-1">
                    Submitted {new Date(report.submittedAt).toLocaleString()}
                  </div>
                )}
              </div>
            </div>

            {saveMessage && (
              <div className="rounded-md border border-green-200 bg-green-50 px-3 py-2 text-sm text-green-700">
                {saveMessage}
              </div>
            )}
            {formError && (
              <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
                {formError}
              </div>
            )}
            {!canEdit && (
              <div className="rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-600">
                This report is read-only in its current status.
              </div>
            )}

            <section className="rounded-lg border border-amber-100 bg-amber-50/40 p-4 space-y-4">
              <div>
                <h3 className="text-md font-semibold text-gray-900">Their month in ministry</h3>
                <p className="text-sm text-gray-600 mt-1">
                  Start with encouragement—what God is doing and how we can pray and help.
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-800">
                  What’s one thing to celebrate?
                </label>
                <textarea
                  rows={4}
                  disabled={!canEdit}
                  value={ministryHighlights}
                  onChange={(e) => setMinistryHighlights(e.target.value)}
                  className="mt-1 block w-full rounded-md border-gray-300 text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-800">
                  What should we pray about?
                </label>
                <textarea
                  rows={3}
                  disabled={!canEdit}
                  value={prayerRequests}
                  onChange={(e) => setPrayerRequests(e.target.value)}
                  className="mt-1 block w-full rounded-md border-gray-300 text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-800">
                  How can the network help?
                </label>
                <textarea
                  rows={2}
                  disabled={!canEdit}
                  value={supportNeeded}
                  onChange={(e) => setSupportNeeded(e.target.value)}
                  className="mt-1 block w-full rounded-md border-gray-300 text-sm"
                />
              </div>
            </section>

            <section>
              <h3 className="text-md font-semibold text-gray-900 border-b pb-2 mb-2">This month at a glance</h3>
              <p className="text-sm text-gray-600 mb-4">
                Quick counts—not a scorecard. Zero is normal in many seasons.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-700">New members</label>
                  <input
                    type="number"
                    min={0}
                    disabled={!canEdit}
                    value={newMembers}
                    onChange={(e) => setNewMembers(Number.parseInt(e.target.value, 10) || 0)}
                    className="mt-1 block w-full rounded-md border-gray-300 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700">Baptisms</label>
                  <input
                    type="number"
                    min={0}
                    disabled={!canEdit}
                    value={baptisms}
                    onChange={(e) => setBaptisms(Number.parseInt(e.target.value, 10) || 0)}
                    className="mt-1 block w-full rounded-md border-gray-300 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700">New faith decisions</label>
                  <input
                    type="number"
                    min={0}
                    disabled={!canEdit}
                    value={salvations}
                    onChange={(e) => setSalvations(Number.parseInt(e.target.value, 10) || 0)}
                    className="mt-1 block w-full rounded-md border-gray-300 text-sm"
                  />
                </div>
              </div>
            </section>

            <section>
              <h3 className="text-md font-semibold text-gray-900 border-b pb-2 mb-2">Giving &amp; budget receipts</h3>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {incomeField('congregationalOffering', 'Congregational offering')}
                {incomeField('sponsoringChurches', 'Sponsoring church(es)')}
                {incomeField('associations', 'Association(s)')}
                {incomeField('mbcb', 'MBCB support')}
                {incomeField('otherSupport', 'Other support')}
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-0.5">Total budget receipts</label>
                  <input
                    type="text"
                    readOnly
                    tabIndex={-1}
                    value={`$${budgetReceiptsTotal.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                    className="block w-full rounded-md border border-gray-200 bg-gray-50 text-gray-900 text-sm py-2 px-3 cursor-default"
                  />
                  <p className="text-xs text-gray-500 mt-0.5">Sum of the categories above (auto-calculated).</p>
                </div>
              </div>
            </section>

            <section>
              <h3 className="text-md font-semibold text-gray-900 border-b pb-2 mb-2">Mission giving</h3>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {missionField('cooperativeProgram', 'Cooperative Program')}
                {missionField('associations', 'Association(s)')}
                {missionField('margaretLackey', 'Margaret Lackey')}
                {missionField('otherMissionCauses', 'Other mission causes')}
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-0.5">Total mission giving</label>
                  <input
                    type="text"
                    readOnly
                    tabIndex={-1}
                    value={`$${missionGivingTotal.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                    className="block w-full rounded-md border border-gray-200 bg-gray-50 text-gray-900 text-sm py-2 px-3 cursor-default"
                  />
                  <p className="text-xs text-gray-500 mt-0.5">Sum of mission lines above (auto-calculated).</p>
                </div>
              </div>
              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-800">
                  Optional: note anything unusual about giving
                </label>
                <textarea
                  rows={2}
                  disabled={!canEdit}
                  value={givingNotes}
                  onChange={(e) => setGivingNotes(e.target.value)}
                  className="mt-1 block w-full rounded-md border-gray-300 text-sm"
                />
              </div>
            </section>

            <section>
              <h3 className="text-md font-semibold text-gray-900 border-b pb-2 mb-2">Worship attendance by Sunday</h3>
              <p className="text-sm text-gray-600 mb-4">
                Rough headcount by Sunday helps notice rhythm—holidays, rest, travel—not one-off judgment.
              </p>
              {worshipBySundaySection()}
              <p className="text-sm text-gray-700 mt-2">
                Month total: <strong>{sumWorship(weekly)}</strong>
              </p>
              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-800">
                  Optional: note anything unusual about attendance
                </label>
                <textarea
                  rows={2}
                  disabled={!canEdit}
                  value={attendanceNotes}
                  onChange={(e) => setAttendanceNotes(e.target.value)}
                  className="mt-1 block w-full rounded-md border-gray-300 text-sm"
                />
              </div>
            </section>

            <section className="rounded-lg border border-emerald-100 bg-emerald-50/30 p-4 space-y-4">
              <h3 className="text-md font-semibold text-gray-900">Generosity &amp; mission (story)</h3>
              <div>
                <label className="block text-sm font-medium text-gray-800">
                  How did you see generosity in your people this month?
                </label>
                <textarea
                  rows={4}
                  disabled={!canEdit}
                  value={additionalNotes}
                  onChange={(e) => setAdditionalNotes(e.target.value)}
                  className="mt-1 block w-full rounded-md border-gray-300 text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-800">
                  Optional: one hope or focus for the month ahead
                </label>
                <textarea
                  rows={2}
                  disabled={!canEdit}
                  value={nextMonthFocus}
                  onChange={(e) => setNextMonthFocus(e.target.value)}
                  className="mt-1 block w-full rounded-md border-gray-300 text-sm"
                />
              </div>
            </section>

            <div className="flex flex-wrap gap-3 pt-4 border-t">
              <button
                type="button"
                onClick={() => saveMutation.mutate()}
                disabled={!canEdit || saveMutation.isPending || submitMutation.isPending}
                className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
              >
                {saveMutation.isPending ? 'Saving…' : 'Save draft'}
              </button>
              <button
                type="button"
                onClick={() => submitMutation.mutate()}
                disabled={!canSubmit || saveMutation.isPending || submitMutation.isPending}
                className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50"
              >
                {submitMutation.isPending ? 'Sending…' : 'Share with team'}
              </button>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default PlanterMbcbReportEditor;
